import Button from "@ui/button/Button"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { useCallback, useEffect, useState } from "react"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { linkRow, Muted, OpenCell, Page, Paged, Panel, State, Table } from "./adminUi"

interface FundsIssuer {
	issuer: number
	issuer_label: string
	provider_code: string
	account: { balance_cents: number | null; error: string | null }
	clients: { companies: number; buyers: number; company_cents: number; buyers_cents: number; total_cents: number }
	cards: { count: number; issuer_cents: number; trx_cents: number; diff_cents: number }
	check: { surplus_cents: number | null; status: "surplus" | "shortfall" | "even" | "unknown" }
	in_system_cents: number
}

interface FundsOverview {
	currency: string
	generated_at: string
	issuers: FundsIssuer[]
	errors: string[]
}

interface FundsCompanyRow {
	company_uid: string
	company_name: string
	issuer: number
	company_cents: number
	buyers_cents: number
	total_cents: number
	buyers: number
	cards: number
}

interface FundsCompaniesPage {
	rows: FundsCompanyRow[]
	summary: { companies: number; company_cents: number; buyers_cents: number; total_cents: number; cards: number }
}

const CHECK_TEXT = { surplus: "Account holds more", shortfall: "Account holds less", even: "Even", unknown: "Unknown" }

/** Our account at each issuer next to what companies, buyers and cards hold on it (system/funds/*). */
export const SuperAdminIssuerFunds = ({ token, onOpenCompany }: { token: string; onOpenCompany: (uid: string) => void }) => {
	const [overview, setOverview] = useState<FundsOverview | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const [issuer, setIssuer] = useState<FundsIssuer | null>(null)
	const [companies, setCompanies] = useState<FundsCompaniesPage | null>(null)
	const [companiesError, setCompaniesError] = useState<string | null>(null)
	const money = (cents: number | null) => cents === null ? "—" : formatCurrencyWithNumberToSymbolWithNumber(cents, overview?.currency || "USD")

	const load = useCallback(async (refresh = false) => {
		setLoading(true)
		setError(null)
		const response = await systemPost<FundsOverview>(token, "system/funds/overview", { refresh })
		setLoading(false)
		if (response.error) return setError(systemErrorMessage(response.error))
		setOverview(response.data)
		setIssuer((current) => response.data.issuers.find((row) => row.issuer === current?.issuer) ?? null)
	}, [token])

	useEffect(() => { void load() }, [load])

	useEffect(() => {
		if (!issuer) return setCompanies(null)
		setCompanies(null)
		setCompaniesError(null)
		void systemPost<FundsCompaniesPage>(token, "system/funds/companies", { issuer: issuer.issuer, nonzero: true, with_meta: true }).then((response) =>
			response.error ? setCompaniesError(systemErrorMessage(response.error)) : setCompanies(response.data))
	}, [token, issuer, overview])

	return <Page>
		<Panel
			title='Our balances at each issuer'
			hint={`Real account at the issuer next to what our clients hold on it, ${overview?.currency ?? "USD"}. Click an issuer to see every company.`}
			actions={<Button variant='secondarySmall' onClick={() => void load(true)} disabled={loading}>Refresh</Button>}
		>
			<State loading={loading && !overview} error={error} empty={overview?.issuers.length === 0} />
			{overview && <div style={{ overflowX: "auto" }}>
				<Table>
					<thead><tr><th>Issuer account</th><th>Real account at issuer</th><th>Companies</th><th>Buyers</th><th>Cards at issuer</th><th>All our clients</th><th>Account minus companies and buyers</th><th /></tr></thead>
					<tbody>{overview.issuers.map((row) => <tr key={row.issuer} {...linkRow(() => setIssuer(row))} aria-selected={issuer?.issuer === row.issuer}>
						<td style={{ fontWeight: 600 }}>{row.issuer_label}<Muted style={{ display: "block", fontSize: 12, fontWeight: 400 }}>{row.provider_code}</Muted></td>
						<td style={{ fontWeight: 600 }}>{row.account.error ? <span style={{ color: T.danger }}>Could not load<Muted style={{ display: "block", fontSize: 12 }}>{row.account.error}</Muted></span> : money(row.account.balance_cents)}</td>
						<td>{money(row.clients.company_cents)}<Muted style={{ display: "block", fontSize: 12 }}>{row.clients.companies} companies</Muted></td>
						<td>{money(row.clients.buyers_cents)}<Muted style={{ display: "block", fontSize: 12 }}>{row.clients.buyers} buyers</Muted></td>
						<td>{money(row.cards.issuer_cents)}<Muted style={{ display: "block", fontSize: 12 }}>{row.cards.count} live cards</Muted></td>
						<td style={{ fontWeight: 600 }}>{money(row.in_system_cents)}</td>
						<td style={{ color: row.check.status === "shortfall" ? T.danger : undefined }}>{money(row.check.surplus_cents)}<Muted style={{ display: "block", fontSize: 12 }}>{CHECK_TEXT[row.check.status]}</Muted></td>
						<OpenCell />
					</tr>)}</tbody>
				</Table>
				{overview.errors.length > 0 && <Muted style={{ display: "block", marginTop: 10, fontSize: 13 }}>Partial data: {overview.errors.join("; ")}</Muted>}
				<Muted style={{ display: "block", marginTop: 10, fontSize: 12.5 }}>Counted {new Date(overview.generated_at).toLocaleString()}. Card money sits on the cards at the issuer, not on our account, so it is not subtracted.</Muted>
			</div>}
		</Panel>
		{issuer && <Panel
			title={`Companies on ${issuer.issuer_label}`}
			hint='Company balance and the balances of its buyers, from transactions. Companies with nothing on this issuer are hidden. Click a company to open it.'
			actions={<Button variant='secondarySmall' onClick={() => setIssuer(null)}>Close</Button>}
		>
			<State loading={!companies && !companiesError} error={companiesError} empty={companies?.rows.length === 0} emptyText='No company holds money on this issuer.' />
			{companies && companies.rows.length > 0 && <>
				<Muted style={{ display: "block", marginBottom: 10, fontSize: 13 }}>
					Total: {money(companies.summary.total_cents)} — companies {money(companies.summary.company_cents)}, buyers {money(companies.summary.buyers_cents)}, {companies.summary.cards} live cards
				</Muted>
				<Paged rows={companies.rows}>{(rows) => <div style={{ overflowX: "auto" }}><Table>
					<thead><tr><th>Company</th><th>Company balance</th><th>Buyers balance</th><th>Total</th><th>Live cards</th><th /></tr></thead>
					<tbody>{rows.map((row) => <tr key={row.company_uid} {...linkRow(() => onOpenCompany(row.company_uid))}>
						<td style={{ fontWeight: 600 }}>{row.company_name}<Muted style={{ display: "block", fontSize: 12, fontWeight: 400 }}>{row.company_uid}</Muted></td>
						<td>{money(row.company_cents)}</td>
						<td>{money(row.buyers_cents)}<Muted style={{ display: "block", fontSize: 12 }}>{row.buyers} buyers</Muted></td>
						<td style={{ fontWeight: 600 }}>{money(row.total_cents)}</td>
						<td>{row.cards}</td>
						<OpenCell />
					</tr>)}</tbody>
				</Table></div>}</Paged>
			</>}
		</Panel>}
	</Page>
}
